import { Injectable } from '@angular/core';
import { MatterFileType } from '@models/matter';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { MatterWordDocumentsRepo } from '@repos/matter-word-documents/matter-word-documents.repo';
import { MatterRepo } from '@repos/matter/matter.repo';
import { AuthState } from '@state/auth/auth.state';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { UserSelectors } from '@state/global/user';
import { BusinessUserRole } from '@models/businesses';
import { Observable, forkJoin, of } from 'rxjs';
import {
	catchError,
	map,
	switchMap,
	take,
	tap,
	finalize,
} from 'rxjs/operators';

export class LoadUserMattersUsage {
	static readonly type = '[UserUsage] Load User Matters';
	constructor(
		public matterType?: MatterFileType,
		public timeRangeInDays = 180,
	) {}
}

export class ClearUserMattersUsage {
	static readonly type = '[UserUsage] Clear User Matters';
}

export interface UserUsageMatter {
	id: string;
	fileType: MatterFileType;
	createdAt: number;
}

export interface UserUsageMatterWordDocument {
	id: string;
	source: string;
	createdAt?: number;
	lastEditedAt?: number;
}

export interface UserMattersUsageStateModel {
	userUsageMatters: UserUsageMatter[];
	userUsageMatterWordDocuments: {
		[matterId: string]: UserUsageMatterWordDocument[];
	};
	loading: boolean;
	error: string | null;
}

@State<UserMattersUsageStateModel>({
	name: 'userMattersUsage',
	defaults: {
		userUsageMatters: [],
		userUsageMatterWordDocuments: {},
		loading: false,
		error: null,
	},
})
@Injectable()
export class UserMattersUsageState {
	constructor(
		private matterRepo: MatterRepo,
		private matterWordDocumentsRepo: MatterWordDocumentsRepo,
		private store: Store,
	) {}

	@Selector()
	static userUsageMatters(
		state: UserMattersUsageStateModel,
	): UserUsageMatter[] {
		return state.userUsageMatters;
	}

	@Selector()
	static loading(state: UserMattersUsageStateModel): boolean {
		return state.loading;
	}

	@Selector()
	static error(state: UserMattersUsageStateModel): string | null {
		return state.error;
	}

	@Selector()
	static userUsageMatterWordDocuments(state: UserMattersUsageStateModel): {
		[matterId: string]: UserUsageMatterWordDocument[];
	} {
		return state.userUsageMatterWordDocuments;
	}

	@Action(LoadUserMattersUsage)
	loadUserMattersUsage(
		ctx: StateContext<UserMattersUsageStateModel>,
	): Observable<{
		userUsageMatters: UserUsageMatter[];
		userUsageMatterWordDocuments: Record<string, UserUsageMatterWordDocument[]>;
	}> {
		const state = ctx.getState();

		if (state.loading) {
			return of({ userUsageMatters: [], userUsageMatterWordDocuments: {} });
		}

		ctx.patchState({ loading: true, error: null });
		const MAX_MATTERS_TO_LOAD = 5;
		const matterType = this.store.selectSnapshot(SelectedMatter.matterFileType);
		const user = this.store.selectSnapshot(AuthState.getLoggedInUser);
		const currentUser = this.store.selectSnapshot(UserSelectors.getCurrentUser);

		if (!user || !user.uid) {
			ctx.patchState({ loading: false, error: 'No user logged in' });
			return of({ userUsageMatters: [], userUsageMatterWordDocuments: {} });
		}

		return this.matterRepo
			.All(null, (q) => {
				let query = q
					.where('fileType', '==', matterType)
					.where('closingDate', '<=', new Date().toISOString());

				if (currentUser?.role === BusinessUserRole.CLERK) {
					query = query.where('clerkAssigned.id', '==', user.uid);
				} else if (currentUser?.role === BusinessUserRole.LAWYER) {
					query = query.where('lawyerAssigned.id', '==', user.uid);
				} else {
					query = query.where('createdBy.id', '==', user.uid);
				}

				return query.orderBy('closingDate', 'desc').limit(MAX_MATTERS_TO_LOAD);
			})
			.pipe(
				take(1),
				map((userMatters) =>
					userMatters.map(
						(matter): UserUsageMatter => ({
							id: matter.id,
							fileType: matter.fileType,
							createdAt: matter.createdAt,
						}),
					),
				),
				switchMap((userUsageMatters) => {
					const wordDocumentQueries = userUsageMatters.map((matter) =>
						this.matterWordDocumentsRepo.All({ matterId: matter.id }).pipe(
							map((documents) => ({
								matterId: matter.id,
								documents: documents
									.filter((doc) => doc.createdBy === user.uid)
									.map(
										(doc): UserUsageMatterWordDocument => ({
											id: doc.id,
											source: doc.source,
											createdAt: doc.createdAt,
											lastEditedAt: doc.updatedAt,
										}),
									),
							})),
							catchError((err) => {
								console.error(
									`Error fetching documents for matter ${matter.id}:`,
									err,
								);
								return of({ matterId: matter.id, documents: [] });
							}),
							take(1),
						),
					);

					return forkJoin(wordDocumentQueries).pipe(
						map((results) => {
							const userUsageMatterWordDocuments: Record<
								string,
								UserUsageMatterWordDocument[]
							> = {};
							for (const { matterId, documents } of results) {
								userUsageMatterWordDocuments[matterId] = documents;
							}
							return { userUsageMatters, userUsageMatterWordDocuments };
						}),
						catchError((err) => {
							console.error('Error in forkJoin for word documents:', err);
							return of({ userUsageMatters, userUsageMatterWordDocuments: {} });
						}),
						take(1),
					);
				}),
				tap(({ userUsageMatters, userUsageMatterWordDocuments }) => {
					ctx.patchState({
						userUsageMatters: [...userUsageMatters],
						userUsageMatterWordDocuments: { ...userUsageMatterWordDocuments },
						loading: false,
					});
				}),
				catchError((err) => {
					ctx.patchState({
						error: err.message || 'Failed to load matters',
						loading: false,
					});
					return of({ userUsageMatters: [], userUsageMatterWordDocuments: {} });
				}),
				finalize(() => {
					ctx.patchState({ loading: false });
				}),
			);
	}

	@Action(ClearUserMattersUsage)
	clearUserMattersUsage(ctx: StateContext<UserMattersUsageStateModel>) {
		ctx.patchState({
			userUsageMatters: [],
			userUsageMatterWordDocuments: {},
			loading: false,
		});
	}
}
